import Image, { ImageProps } from 'next/image'
import DOMPurify from 'dompurify'
import { memo, useEffect, useMemo, useState } from 'react'
import { PhotoIcon } from '@heroicons/react/20/solid'

function sanitizeUrl(url: string) {
  return DOMPurify.sanitize(url, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
}

// original: Loads the originally requested image using the usual next/image optimization
// fallback-unoptimized: Loads the originally requested image, but with optimizations disabled. This is useful for
//                       SVG images that don't have an `.svg` suffix in URL, next will refuse to load them in optimized
//                       mode. If the URL already has the `.svg` suffix, next will load it in unoptimized mode by default.
// fallback: Fallback to our logo when original and unoptimized image fails to load
type ImageLoadConfig = 'original' | 'fallback-unoptimized' | 'fallback'

type ImageWithFallbackProps = Pick<
  ImageProps,
  'alt' | 'width' | 'height' | 'className'
> & {
  src?: string
}

const ImageWithFallback = memo(function ImageWithFallback({
  src,
  alt,
  width,
  height,
  className = '',
}: ImageWithFallbackProps) {
  src = src?.startsWith('http:') ? src.replace('http:', 'https:') : src
  const [imageLoadState, setImageLoadState] = useState<ImageLoadConfig>(
    src ? 'original' : 'fallback',
  )

  useEffect(() => {
    // Reset load state when src changes
    setImageLoadState(src ? 'original' : 'fallback')
  }, [src])

  const handleError = () => {
    if (imageLoadState === 'original') {
      setImageLoadState('fallback-unoptimized')
    }
    if (imageLoadState === 'fallback-unoptimized') {
      setImageLoadState('fallback')
    }
  }

  const srcWithFallback = useMemo(
    () =>
      imageLoadState !== 'fallback' && src ? sanitizeUrl(src.toString()) : '',
    [src, imageLoadState],
  )

  return imageLoadState !== 'fallback' ? (
    <Image
      className={`${className} object-contain`}
      style={{ maxWidth: `${width}px`, maxHeight: `${height}px` }}
      src={srcWithFallback}
      alt={alt}
      width={width}
      height={height}
      //for now we pay to much for vercel image optimization, so we disable it
      unoptimized={true}
      onError={handleError}
    />
  ) : (
    <PhotoIcon
      className={`${className} text-th-fgd-4`}
      style={{
        minWidth: `${width}px`,
        minHeight: `${height}px`,
        width: `${width}px`,
        height: `${height}px`,
      }}
    />
  )
})
export default ImageWithFallback
